import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Draggable from 'react-draggable';
import { identity, noop, pick, without } from 'lodash';
import cn from 'classnames';
import useMemoObject from '@dyl/hooks/useMemoObject';
import { alphaCheckerBackground } from '@dyl/styling/common';
import Conditional from '../Conditional';
import useSize from "@dyl/hooks/useSize";

const TARGET_DIMENSIONS = { width: 1024, height: 768 };

const DragCanvasContext = createContext({
    scale: 1,
    /**
     * @param {() => void} cb
     * @returns {() => void} Unregister function
     */
    registerCoordinateFix(cb) { throw new Error('No DragCanvasContext in tree.'); },
});

/**
 * @param {{ backgroundSrc: string } & React.HTMLAttributes<HTMLDivElement>} props
 */
export default function DragCanvas({
    children,
    backgroundSrc,
    initCoords,
    className: propsClass,
    targetDimensions = TARGET_DIMENSIONS
}) {

    /** @type {React.MutableRefObject<HTMLDivElement>} */
    const canvasRef = useRef();

    // Scale of the viewport in the editor screen
    const [scale] = useState(1);

    const [coordinateAutoFixes, setCoordinateAutoFixes] = useState([]);

    const registerCoordinateFix = useCallback(cb => {
        setCoordinateAutoFixes(prev => [...prev, cb]);

        return () => setCoordinateAutoFixes(prev => without(prev, cb));
    }, []);

    const context = useMemoObject({ scale, registerCoordinateFix });

    const onAutoFixClick = () => {
        for (const fix of coordinateAutoFixes) {
            try {
                fix();
            }
            catch (error) {
                console.error(error);
            }
        }
    };

    // Either set initial position and use original image size or scale to 100%
    const imageMode = !!initCoords ? `${initCoords.left}px ${initCoords.top}px` : 'top left/100%';

    return (
        <>
            <Conditional if={coordinateAutoFixes.length > 0}>
                <Button
                    onClick={onAutoFixClick}
                    color='primary'
                    variant='contained'
                >
                    Reset out-of-range coordinates
                </Button>
            </Conditional>
            <div
                ref={canvasRef}
                className={cn(propsClass)}
                style={{
                    width: `${targetDimensions.width}px`,
                    height: `${targetDimensions.height}px`,
                    background: [
                        backgroundSrc && `url(${backgroundSrc}) ${imageMode} no-repeat`,
                        alphaCheckerBackground,
                    ].filter(identity).join(', '),
                }}
            >
                <DragCanvasContext.Provider value={context}>
                    {
                        React.Children.map(children,
                            child =>
                                React.isValidElement(child) ? React.cloneElement(child, { targetDimensions }) : child)
                    }
                </DragCanvasContext.Provider>
            </div>
        </>
    );
}


const useImageStyles = makeStyles(theme => ({
    root: {
        position: 'absolute',

        '&:not($disabled)': {
            cursor: 'grab',

            transition: theme.transitions.create('filter', { duration: 80 }),
            '&:hover': {
                filter: [
                    ...(() => {
                        const width = 1;
                        const filters = [];

                        filters.push('brightness(110%)');

                        // Oddly, -1 and +2 make for more consistent offsets
                        for (let x = -1; x < 3; x += 3)
                            for (let y = -1; y < 3; y += 3) {
                                filters.push(`drop-shadow(${x * width}px ${y * width}px 0 ${theme.palette.primary.main})`);
                            }

                        return filters;
                    })(),
                ].join(' '),
            },
        },
    },
    disabled: {
        pointerEvents: 'none',
    },
    image: {
        pointerEvents: 'none',
        transformOrigin: 'top left',
        userSelect: 'none',
    },
}))

/**
 * @param {Object} props
 * @param {string} [props.src]
 * @param {string} [props.alt]
 * @param {{ top: number, left: number }} [props.coordinates]
 * @param {(coordinates: { top: number, left: number }) => void} [props.onCoordinatesChange]
 */
export function DragCanvasImage(
    {
        src,
        alt,
        coordinates: rawCoordinates,
        onCoordinatesChange = noop,
        disabled = false,
        imageStyle = {},
        text = null,
        targetDimensions = TARGET_DIMENSIONS
}) {
    const classes = useImageStyles();
    const { scale, registerCoordinateFix } = useContext(DragCanvasContext);

    const [draggableSize, setDraggableSize] = useState({ width: 0, height: 0 });
    const [scaledDraggableSize, setScaledDraggableSize] = useState(null);

    const onImageLoad = useCallback((event, ...rest) => {
        const { naturalWidth: width, naturalHeight: height } = pick(event.target, 'naturalWidth', 'naturalHeight');
        setDraggableSize({ width, height });
        setScaledDraggableSize({
            width: width * scale,
            height: height * scale
        });
    }, [src]);

    /**
     * Bounds which ensure the whole movable image remains within
     *  the bounds of the viewport
     */
    const bounds = useMemo(() => ({
        top: 0,
        left: 0,
        bottom: (targetDimensions.height - draggableSize?.height ?? 0) * scale,
        right: (targetDimensions.width - draggableSize?.width ?? 0) * scale,
    }), [draggableSize, scale]);

    /** Coordinates in screen editor space. */
    const coordinates = {
        x: (rawCoordinates?.left ?? 0) * scale,
        y: (rawCoordinates?.top ?? 0) * scale,
    };

    // Register auto-fix for out-of-range coordinates
    useEffect(() => {
        const top = rawCoordinates?.top ?? 0;
        const left = rawCoordinates?.left ?? 0;
        const right = left + draggableSize.width;
        const bottom = top + draggableSize.height;

        const isBroken = (
            top < 0
            || left < 0
            || right > targetDimensions.width
            || bottom > targetDimensions.height
        );

        if (!isBroken) return;

        const unregister = registerCoordinateFix(() => {
            onCoordinatesChange({ top: 0, left: 0 });
        });

        return () => unregister();
    }, [rawCoordinates, onCoordinatesChange, draggableSize]);

    const onStopDragging = useCallback(
        /** @type {import('react-draggable').DraggableProps['onStop']} */
        (event, dragData) => {
            // Push update into the task object only on drag stop
            onCoordinatesChange({
                // Scale coordinates from screen space back to viewport space
                top: Math.round(dragData.y / scale),
                left: Math.round(dragData.x / scale),
            });
        }, [scale, onCoordinatesChange]
    );

    return (
        // Draggable container for the image
        <Draggable
            bounds={bounds}
            position={coordinates}
            onStop={onStopDragging}
            disabled={disabled}
            // We scale manually, so don't use this prop, it actually makes things worse
            // scale={scale}
        >
            {/* Wrapping div is required for avoiding default
              *     drag behaviour, and allowing the
              *     secondary transform on the image.
              * Drawing hidden elements in order to trigger image load
              *     and actually show the draggable once it loaded.
              */}
            <div
                className={cn(classes.root, disabled && classes.disabled)}
                style={
                    !scaledDraggableSize
                        ? { contentVisibility: 'hidden' }
                        : { ...scaledDraggableSize, overflow: 'visible' }
                }>
                <img
                    className={classes.image}
                    src={src}
                    alt={alt}
                    onLoad={onImageLoad}
                    style={scaledDraggableSize
                        ? { transform: `scale(${scale})`, ...imageStyle }
                        : {}}
                />
                {scaledDraggableSize && text &&
                    <div
                        style={{
                            opacity: 0.45,
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            top: 0,
                            textAlign: 'center'
                        }}>
                        <Typography variant='h2'>{text}</Typography>
                    </div>
                }
            </div>
        </Draggable>
    );
}

/**
 * @param {Object} props
 * @param {string} [props.src]
 * @param {string} [props.alt]
 * @param {{ top: number, left: number }} [props.coordinates]
 * @param {(coordinates: { top: number, left: number }) => void} [props.onCoordinatesChange]
 */
export function DragCanvasText(
    {
        coordinates: rawCoordinates,
        onCoordinatesChange = noop,
        disabled = false,
        text = null,
        style = {},
        targetDimensions = TARGET_DIMENSIONS
    }) {
    const classes = useImageStyles();
    const { scale, registerCoordinateFix } = useContext(DragCanvasContext);

    const [draggableSize] = useState({ width: 0, height: 0 });

    /**
     * Bounds which ensure the whole movable image remains within
     *  the bounds of the viewport
     */
    const bounds = useMemo(() => ({
        top: 0,
        left: 0,
        bottom: (targetDimensions.height - draggableSize?.height ?? 0) * scale,
        right: (targetDimensions.width - draggableSize?.width ?? 0) * scale,
    }), [draggableSize, scale]);

    /** Coordinates in screen editor space. */
    const coordinates = {
        x: (rawCoordinates?.left ?? 0) * scale,
        y: (rawCoordinates?.top ?? 0) * scale,
    };

    // Register auto-fix for out-of-range coordinates
    useEffect(() => {
        const top = rawCoordinates?.top ?? 0;
        const left = rawCoordinates?.left ?? 0;
        const right = left + draggableSize.width;
        const bottom = top + draggableSize.height;

        const isBroken = (
            top < 0
            || left < 0
            || right > targetDimensions.width
            || bottom > targetDimensions.height
        );

        if (!isBroken) return;

        const unregister = registerCoordinateFix(() => {
            onCoordinatesChange({ top: 0, left: 0 });
        });

        return () => unregister();
    }, [rawCoordinates, onCoordinatesChange, draggableSize]);

    const onStopDragging = useCallback(
        /** @type {import('react-draggable').DraggableProps['onStop']} */
        (event, dragData) => {
            // Push update into the task object only on drag stop
            onCoordinatesChange({
                // Scale coordinates from screen space back to viewport space
                top: Math.round(dragData.y / scale),
                left: Math.round(dragData.x / scale),
            });
        }, [scale, onCoordinatesChange]
    );

    return (
        // Draggable container for the image
        <Draggable
            bounds={bounds}
            position={coordinates}
            onStop={onStopDragging}
            disabled={disabled}
            // We scale manually, so don't use this prop, it actually makes things worse
            // scale={scale}
        >
            <div
                className={cn(classes.root, disabled && classes.disabled)}
                style={{ overflow: 'visible' }}>
                <div
                    style={{
                        opacity: 0.64,
                        top: 0,
                        textAlign: 'center',
                        position: 'absolute',
                        zIndex: -1,
                        boxShadow: '0px 0px 10px rgba(0, 0, 0, 1)',
                        width: `${(text?.length ?? 0) * 12 + 32}px`,
                        ...style
                    }}>
                    <Typography
                        variant='h2'
                        style={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: '#003553',
                            background: '#FFF',
                            fontSize: '24px',
                            lineHeight: '28px',
                            fontFamily: 'CUP_Infant',
                            width: `${(text?.length ?? 0) * 12 + 32}px`,
                            ...style
                        }}
                    >
                        {style?.letter && (
                            <span style={{ fontSize: '20px', opacity: 0.7, lineHeight: '24px' }}>
                                {style.letter}
                            </span>
                        )}
                        <span style={{ fontSize: '22px', textAlign: 'center' }}>{text}</span>
                    </Typography>
                </div>
            </div>
        </Draggable>
    );
}


export function DragCanvasBubble(
{
    coordinates: rawCoordinates,
    onCoordinatesChange = noop,
    disabled = false,
    text = null,
    style = {},
    targetDimensions = TARGET_DIMENSIONS
}) {
    const classes = useImageStyles();
    const bubbleRef = useRef();
    const draggableSize = useSize(bubbleRef);
    // const [draggableSize] = useState({width: 316, height: 80});

    const { scale, registerCoordinateFix } = useContext(DragCanvasContext);

    /**
     * Bounds which ensure the whole movable image remains within
     *  the bounds of the viewport
     */
    const bounds = useMemo(() => ({
        top: 0,
        left: 0,
        bottom: (targetDimensions.height - draggableSize?.height ?? 0) * scale,
        right: (targetDimensions.width - draggableSize?.width ?? 0) * scale,
    }), [draggableSize, scale]);

    /** Coordinates in screen editor space. */
    const coordinates = {
        x: (rawCoordinates?.left ?? 0) * scale,
        y: (rawCoordinates?.top ?? 0) * scale,
    };

    // Register auto-fix for out-of-range coordinates
    useEffect(() => {
        const top = rawCoordinates?.top ?? 0;
        const left = rawCoordinates?.left ?? 0;
        const right = left + draggableSize.width;
        const bottom = top + draggableSize.height;

        const isBroken = (
            top < 0
            || left < 0
            || right > targetDimensions.width
            || bottom > targetDimensions.height
        );

        if (!isBroken) return;

        const unregister = registerCoordinateFix(() => {
            onCoordinatesChange({ top: 0, left: 0 });
        });

        return () => unregister();
    }, [rawCoordinates, onCoordinatesChange, draggableSize]);

    const onStopDragging = useCallback(
        /** @type {import('react-draggable').DraggableProps['onStop']} */
        (event, dragData) => {
            // Push update into the task object only on drag stop
            onCoordinatesChange({
                // Scale coordinates from screen space back to viewport space
                top: dragData.y / scale,
                left: dragData.x / scale,
            });
        }, [scale, onCoordinatesChange]
    );

    return (
        // Draggable container for the image
        <Draggable
            bounds={bounds}
            position={coordinates}
            onStop={onStopDragging}
            disabled={disabled}
            // We scale manually, so don't use this prop, it actually makes things worse
            // scale={scale}
        >
            <div
                className={cn(classes.root, disabled && classes.disabled)}
                style={{ overflow: 'visible' }}>
                <div
                    ref={bubbleRef}
                    style={{
                        opacity: 1,
                        top: 0,
                        position: 'absolute',
                        zIndex: -1,
                        minHeight: '80px',
                        overflow: 'none',
                        width: '316px',
                        ...style
                    }}>

                    <Typography variant='h2' style={{
                        position: 'relative',
                        color: '#003553',
                        fontSize: '24px',
                        background: '#FFF',
                        padding: '12px 16px 12px 16px',
                        width: '316px',
                        minHeight: '80px',
                        whiteSpace: 'break-spaces',
                        overflowWrap: 'break-word',
                        ...style
                    }}>
                        {text}
                    </Typography>
                </div>
            </div>
        </Draggable>
    );
}